// Funkcja do konwersji odpowiedzi AI (markdown) na HTML
export const formatAIResponseToHTML = (text: string): string => {
    if (!text) return '';
    
    // Najpierw podziel na sekcje (pustymi liniami lub nagłówkami)
    let html = text;
    
    // 1. Nagłówki H1, H2, H3 (najpierw te, żeby nie były nadpisywane)
    html = html
        .replace(/^###\s+(.+)$/gm, '<h4 class="text-base font-semibold text-gray-800 mt-6 mb-3 text-indigo-600">$1</h4>')
        .replace(/^##\s+(.+)$/gm, '<h3 class="text-lg font-bold text-gray-900 mt-6 mb-4 pb-2 border-b-2 border-indigo-200">$1</h3>')
        .replace(/^#\s+(.+)$/gm, '<h2 class="text-xl font-bold text-gray-900 mt-6 mb-4 pb-2 border-b-2 border-indigo-300">$1</h2>');
    
    // 2. Sekcje z numeracją (1., 2., 3., etc.) - tylko jeśli zawierają więcej niż kilka słów
    html = html.replace(/^(\d+)\.\s+(.+?)(?=\n\n\n|\n\d+\.\s+[A-ZĄĆĘŁŃÓŚŹŻ]|$)/gms, (match, num, content) => {
        const trimmed = content.trim();
        if (trimmed.length < 20) {
            // Krótkie sekcje - zwykły paragraf
            return `<p class="mb-3 text-gray-700"><strong class="text-indigo-600">${num}.</strong> ${formatParagraph(trimmed)}</p>`;
        }
        // Długie sekcje - wizualny box
        return `<div class="mb-5 p-4 bg-indigo-50 rounded-lg border-l-4 border-indigo-500 shadow-sm">
            <div class="flex items-start gap-3">
                <span class="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-sm">${num}</span>
                <div class="flex-1">${formatParagraph(trimmed)}</div>
            </div>
        </div>`;
    });
    
    // 3. Listy z myślnikiem - prostsze podejście
    const listItems: string[] = [];
    html = html.replace(/^[-*]\s+(.+?)$/gm, (match, content) => {
        const formatted = formatParagraph(content.trim());
        listItems.push(`<li class="mb-2 text-gray-700 flex items-start gap-2"><span class="text-indigo-600 font-bold mt-0.5">•</span><span>${formatted}</span></li>`);
        return `__LIST_ITEM_${listItems.length - 1}__`;
    });
    
    // 4. Przywróć elementy listy przed formatowaniem inline
    listItems.forEach((item, index) => {
        html = html.replace(`__LIST_ITEM_${index}__`, item);
    });
    
    // 5. Zgrupuj elementy listy razem
    html = html.replace(/(<li[^>]*>.*?<\/li>\s*)+/gs, (match) => {
        return `<ul class="list-none space-y-2 my-4 ml-2">${match}</ul>`;
    });
    
    // 6. Formatowanie inline (bold, italic, kod) - po listach
    html = formatInlineText(html);
    
    // 7. Podziel na paragrafy (podwójne nowe linie)
    html = html.split(/\n\n+/).map(para => {
        const trimmed = para.trim();
        if (!trimmed) return '';
        
        // Jeśli już jest HTML tag (h2, h3, div, ul), nie opakowuj
        if (trimmed.match(/^<(h[1-6]|div|ul|ol)/)) return trimmed;
        
        // Jeśli to jeszcze nie jest sformatowane jako HTML
        if (!trimmed.startsWith('<')) {
            return `<p class="mb-3 text-gray-700 leading-relaxed">${trimmed}</p>`;
        }
        
        return trimmed;
    }).join('');
    
    // 8. Finalne opakowanie
    html = `<div class="ai-response-content space-y-4">${html}</div>`;
    
    // 9. Usuń puste paragrafy i napraw zagnieżdżenia
    html = html.replace(/<p[^>]*>\s*<\/p>/g, '');
    html = html.replace(/(<ul[^>]*>.*?<\/ul>)\s*<p([^>]*)>/g, '$1');
    
    return html;
};

// Funkcja pomocnicza do formatowania inline text
function formatInlineText(text: string): string {
    return text
        // Bold (**text**) - najpierw bold, potem inne
        .replace(/\*\*([^*]+?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
        // Italic (*text*) - najpierw sprawdź czy to nie lista (prostsze podejście)
        .replace(/\*([^*\n]+?)\*/g, (match, content) => {
            // Pomiń jeśli to jest początek linii z myślnikiem lub gwiazdką
            return match.startsWith('*') && !match.includes('\n') && !match.startsWith('**') 
                ? '<em class="italic text-gray-600">' + content + '</em>' 
                : match;
        })
        // Kod inline (`code`)
        .replace(/`([^`]+)`/g, '<code class="bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded text-xs font-mono border border-indigo-200">$1</code>')
        // Bloki kodu (```...```)
        .replace(/```[\s\S]*?```/g, '')
        // Linki markdown [text](url) - zamień na HTML linki z przyciskami dla /dashboard
        // Dla dashboard używamy delegacji zdarzeń zamiast onclick (bezpieczniejsze dla React)
        .replace(/\[([^\]]+)\]\(\/dashboard\)/g, (match, text) => {
            return `<div class="mt-4"><a href="/#dashboard" data-dashboard-subscription="true" class="inline-block bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-indigo-700 transition-colors no-underline cursor-pointer">${text}</a></div>`;
        })
        .replace(/\[([^\]]+)\]\(#dashboard\)/g, (match, text) => {
            return `<div class="mt-4"><a href="/#dashboard" data-dashboard-subscription="true" class="inline-block bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-indigo-700 transition-colors no-underline cursor-pointer">${text}</a></div>`;
        })
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-indigo-600 hover:text-indigo-800 font-semibold underline">$1</a>')
        // Podkreślenia ważnych informacji
        .replace(/\[!\]\s*(.+)/g, '<div class="bg-yellow-50 border-l-4 border-yellow-400 p-3 my-2 rounded text-sm"><strong>⚠️</strong> $1</div>');
}

// Funkcja pomocnicza do formatowania paragrafu
function formatParagraph(text: string): string {
    if (!text) return '';
    let para = formatInlineText(text);
    // Pojedyncze nowe linie jako <br>
    para = para.replace(/\n(?!\n)/g, '<br/>');
    return para;
}

